import React, { useState, useEffect, useRef } from 'react';
import getQueues from '..//api_scripts/get/getQueues'; // Adjust path if necessary
import { saveAs } from 'file-saver'; // For saving the generated JSON

const QueueMapping = ({ onBackToTools, authData }) => {
  const [queues, setQueues] = useState([]);
  const [selectedQueues, setSelectedQueues] = useState({}); // { queueId: true/false }
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState([]);
  const [generatedJson, setGeneratedJson] = useState(null);
  const [functionNameInput, setFunctionNameInput] = useState('PB_Inbound_Test'); // New state for function name input
  const [searchTerm, setSearchTerm] = useState(''); // New state for search term
  const logContainerRef = useRef(null);

  const { org_id, access_token } = authData;

  // Function to scroll to the bottom of the log
  const scrollToBottom = () => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  };

  // Helper function for logging progress
  const updateProgress = (type, message) => {
    setProgress((prev) => {
      const newProgress = [
        ...prev,
        { type, message, timestamp: new Date().toLocaleTimeString() },
      ];
      return newProgress;
    });
    // Scroll to bottom after state update
    setTimeout(scrollToBottom, 0);
  };

  // Styles for progress messages
  const getMessageStyle = (type) => {
    switch (type) {
      case "info":
        return { color: "#2196f3" }; // Blue
      case "success":
        return { color: "#4caf50" }; // Green
      case "warning":
        return { color: "#ff9800" }; // Orange
      case "error":
        return { color: "#f44336" }; // Red
      default:
        return { color: "#333" }; // Black/default
    }
  };

  useEffect(() => {
    const fetchAllQueues = async () => {
      setLoading(true);
      setProgress([]);
      updateProgress("info", "Fetching all available queues...");
      try {
        // getQueues function expects orgId, accessToken, setProgress
        const fetchedQueues = await getQueues(null, org_id, access_token, setProgress);
        setQueues(fetchedQueues);
        // Select all queues by default
        const initialSelection = fetchedQueues.reduce((acc, queue) => {
          acc[queue.id] = true;
          return acc;
        }, {});
        setSelectedQueues(initialSelection);
        updateProgress("success", `Found ${fetchedQueues.length} queues.`);
      } catch (error) {
        updateProgress("error", `Failed to fetch queues: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (org_id && access_token) {
      fetchAllQueues();
    }
  }, [org_id, access_token, updateProgress]); // Re-fetch if authData changes

  const handleQueueToggle = (queueId) => {
    setSelectedQueues((prevSelected) => ({
      ...prevSelected,
      [queueId]: !prevSelected[queueId],
    }));
  };

  const handleSelectAll = () => {
    const allSelected = queues.reduce((acc, queue) => {
      acc[queue.id] = true;
      return acc;
    }, {});
    setSelectedQueues(allSelected);
  };

  const handleDeselectAll = () => {
    setSelectedQueues({});
  };

  // Filtered queues based on search term
  const filteredQueues = queues.filter(queue =>
    queue.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    queue.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const generateQueueMappingJson = () => {
    if (!functionNameInput.trim()) {
      updateProgress("error", "Function Name cannot be empty.");
      return;
    }

    updateProgress("info", "Generating queue mapping JSON...");
    const queueMapping = {};
    queues.forEach((queue) => {
      if (selectedQueues[queue.id]) {
        queueMapping[queue.name] = {
          queue_id: queue.id,
        };
      }
    });

    const functionName = functionNameInput.trim();
    const generatedSourceCode = `export const handle = async (request, response) => {
    // Do not remove the existing wrapper code, else your code might not execute properly

    // Get the queue name from the input
    const queue = request.inputs.queueName;

    // JSON mapping queue names to their respective queue IDs
    const queueMapping = ${JSON.stringify(queueMapping, null, 4)};

    // Check if the queue exists in our mapping
    if (queueMapping[queue]) {
        // If queue is found, return its specific queue ID
        response.data = queueMapping[queue];
    } else {
        // Handle case where queue is not found (optional)
        response.data = { error: "Queue not found in mapping" };
    }

    // Return the response object
    return response;
};`;

    const jsonOutput = {
      name: functionName,
      language: "js",
      runtime: "nodejs22.x",
      description: "Dynamically generated queue mapping function.",
      sourceCode: generatedSourceCode,
      inputs: [
        {
          name: "queueName",
          dataType: "STRING",
          description: "The name of the queue to look up.",
          defaultValue: "",
        },
      ],
      outputs: '{"queue_id": "queue id value"}', // This is a placeholder, actual output depends on mapping
    };

    setGeneratedJson(jsonOutput);
    updateProgress("success", "Queue mapping JSON generated successfully.");
  };

  const handleDownloadJson = () => {
    if (generatedJson) {
      const fileName = `${generatedJson.name}_QueueMapping.json`;
      const blob = new Blob([JSON.stringify(generatedJson, null, 2)], {
        type: 'application/json',
      });
      saveAs(blob, fileName);
      updateProgress("success", `Downloaded ${fileName}`);
    } else {
      updateProgress("warning", "No JSON to download. Generate it first.");
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: 'auto', fontFamily: 'Inter, sans-serif', backgroundColor: '#f9f9f9', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      <h1 style={{ color: '#333', textAlign: 'center', marginBottom: '25px' }}>Queue Mapping Function</h1>
      <p style={{ textAlign: 'center', color: '#555', marginBottom: '30px' }}>
        This tool helps you generate a Webex Contact Center Function JSON file for queue ID mapping.
      </p>
      <p style={{ textAlign: 'center', color: '#666', fontSize: '0.9em', marginBottom: '20px' }}>
        Authenticated with Org ID: <span style={{ fontWeight: 'bold' }}>{authData.org_id}</span>
      </p>

      {/* Function Name Input */}
      <div style={{ marginBottom: '20px', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', backgroundColor: '#fff' }}>
        <h2 style={{ fontSize: '1.3em', marginBottom: '15px', color: '#333' }}>Function Details:</h2>
        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="functionName" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#444' }}>
            Function Name:
          </label>
          <input
            type="text"
            id="functionName"
            value={functionNameInput}
            onChange={(e) => setFunctionNameInput(e.target.value)}
            placeholder="e.g., MyQueueMapper"
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ccc',
              borderRadius: '5px',
              fontSize: '1em',
            }}
          />
          <small style={{ color: '#666', fontSize: '0.8em', marginTop: '5px', display: 'block' }}>
            This will be the name of your Webex CC Function.
          </small>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', marginBottom: '20px', color: '#007bff' }}>
          Loading queues...
        </div>
      )}

      {!loading && queues.length > 0 && (
        <div style={{ marginBottom: '30px', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', backgroundColor: '#fff' }}>
          <h2 style={{ fontSize: '1.3em', marginBottom: '15px', color: '#333' }}>Select Queues for Mapping:</h2>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap', alignItems: 'center' }}> {/* Added flexWrap and alignItems */}
            <button
              onClick={handleSelectAll}
              style={{
                padding: '8px 15px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '0.9em',
                transition: 'background-color 0.3s ease',
              }}
            >
              Select All
            </button>
            <button
              onClick={handleDeselectAll}
              style={{
                padding: '8px 15px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '0.9em',
                transition: 'background-color 0.3s ease',
              }}
            >
              Deselect All
            </button>
            <input
              type="text"
              placeholder="Search queues..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flexGrow: 1, /* Allow input to take available space */
                padding: '8px 10px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                fontSize: '0.9em',
                minWidth: '150px', /* Ensure it doesn't get too small */
              }}
            />
          </div>
          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '5px', padding: '10px' }}>
            {filteredQueues.map((queue) => ( // Use filteredQueues here
              <div key={queue.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <input
                  type="checkbox"
                  id={`queue-${queue.id}`}
                  checked={!!selectedQueues[queue.id]}
                  onChange={() => handleQueueToggle(queue.id)}
                  style={{ marginRight: '10px', transform: 'scale(1.2)' }}
                />
                <label htmlFor={`queue-${queue.id}`} style={{ fontSize: '1em', color: '#444', flexGrow: 1 }}>
                  {queue.name} <span style={{ fontSize: '0.8em', color: '#888' }}>({queue.id})</span>
                </label>
              </div>
            ))}
            {filteredQueues.length === 0 && searchTerm && (
              <p style={{ textAlign: 'center', color: '#888', marginTop: '10px' }}>No queues match your search.</p>
            )}
          </div>
        </div>
      )}

      {!loading && queues.length === 0 && (
        <p style={{ textAlign: 'center', color: '#dc3545', marginBottom: '20px' }}>
          No queues found or failed to load queues. Please check your authentication and API access.
        </p>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
        <button
          onClick={generateQueueMappingJson}
          disabled={loading || Object.keys(selectedQueues).length === 0 || !functionNameInput.trim()}
          style={{
            padding: '12px 25px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: (loading || Object.keys(selectedQueues).length === 0 || !functionNameInput.trim()) ? 'not-allowed' : 'pointer',
            opacity: (loading || Object.keys(selectedQueues).length === 0 || !functionNameInput.trim()) ? 0.7 : 1,
            fontSize: '1em',
            fontWeight: '600',
            transition: 'background-color 0.3s ease, opacity 0.3s ease',
            boxShadow: '0 2px 8px rgba(40, 167, 69, 0.2)'
          }}
        >
          Generate Function JSON
        </button>

        <button
          onClick={handleDownloadJson}
          disabled={!generatedJson}
          style={{
            padding: '12px 25px',
            backgroundColor: '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: !generatedJson ? 'not-allowed' : 'pointer',
            opacity: !generatedJson ? 0.7 : 1,
            fontSize: '1em',
            fontWeight: '600',
            transition: 'background-color 0.3s ease, opacity 0.3s ease',
            boxShadow: '0 2px 8px rgba(23, 162, 184, 0.2)'
          }}
        >
          Download JSON
        </button>
      </div>

      {generatedJson && (
        <div style={{ borderTop: "1px solid #eee", paddingTop: "20px", marginTop: "20px" }}>
          <h2 style={{ color: '#333', marginBottom: '15px' }}>Generated Function JSON:</h2>
          <pre style={{
            backgroundColor: '#e9ecef',
            padding: '15px',
            borderRadius: '6px',
            maxHeight: '400px',
            overflowY: 'auto',
            border: '1px solid #dee2e6',
            fontSize: '0.85em',
            textAlign: 'left',
            whiteSpace: 'pre-wrap', // Ensures long lines wrap
            wordBreak: 'break-all' // Breaks words if necessary
          }}>
            <code>{JSON.stringify(generatedJson, null, 2)}</code>
          </pre>

          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#d4edda', border: '1px solid #28a745', borderRadius: '6px', textAlign: 'left' }}>
            <h3 style={{ color: '#155724', marginBottom: '10px' }}>Installation and Usage Instructions:</h3>
            <ol style={{ color: '#155724', lineHeight: '1.6', paddingLeft: '20px' }}>
              <li>
                **Import Function Code:** In Webex Control Hub, navigate to "Contact Center" &gt; "Developer" &gt; "Functions". Click "Create Function" and then "Import Function". Upload the downloaded JSON file (`${generatedJson.name}_QueueMapping.json`).
              </li>
              <li>
                **Insert Function Node in Flow:** In your Webex Contact Center flow designer, drag and drop a "Function" node (activity) into your call flow.
              </li>
              <li>
                **Configure Function Node:**
                <ul>
                  <li>
                    **Select Function:** Choose the newly imported function named `{generatedJson.name}`.
                  </li>
                  <li>
                    **Input Variable:** This function requires an input variable for the "Queue Name". Set this to the flow variable that holds the queue name you want to look up (e.g., <code>$.queueNameInput</code>).
                  </li>
                  <li>
                    **Output Variable:** The function will output an object like <code>{'{'} "queue_id": "queue id value" {'}'}</code>. Select the flow variable where you want the <code>queue_id</code> to be assigned (e.g., you can use the same input variable or create a new one like <code>$.selectedQueueId</code>).
                  </li>
                  <li>
                    **Path Expression:** Set the path expression for the output variable to <code>$.queue_id</code>. This will extract just the queue ID from the function's output.
                  </li>
                </ul>
              </li>
            </ol>
          </div>
        </div>
      )}

      <div style={{ borderTop: "1px solid #eee", paddingTop: "20px", marginTop: "20px" }}>
        <h2 style={{ color: "#333", marginBottom: '15px' }}>Progress Log:</h2>
        <div
          ref={logContainerRef}
          style={{
            backgroundColor: '#e9ecef',
            padding: '15px',
            borderRadius: '6px',
            maxHeight: '200px',
            overflowY: 'auto',
            border: '1px solid #dee2e6',
            marginBottom: '20px'
          }}
        >
          <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
            {progress.map((item, index) => (
              <li
                key={index}
                style={{
                  marginBottom: "5px",
                  fontSize: "0.9em",
                  ...getMessageStyle(item.type),
                }}
              >
                <span style={{ fontWeight: "normal", color: "#666" }}>
                  [{item.timestamp}]{" "}
                </span>
                {item.message}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        <button
          onClick={onBackToTools}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '0.9em',
            transition: 'background-color 0.3s ease'
          }}
        >
          Back to Tools
        </button>
      </div>
    </div>
  );
};

export default QueueMapping;